import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check localStorage for saved user session
    const savedUser = localStorage.getItem('unshackled_user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setIsLoading(false);
  }, []);

  // Traditional email/password login (mocked)
  const login = async (email, password) => {
    const users = JSON.parse(localStorage.getItem('unshackled_users') || '[]');
    const foundUser = users.find(u => u.email === email && u.password === password);
    
    if (foundUser) {
      const { password: _, ...userWithoutPassword } = foundUser;
      setUser(userWithoutPassword);
      localStorage.setItem('unshackled_user', JSON.stringify(userWithoutPassword));
      return { success: true };
    }
    return { success: false, error: 'Invalid email or password' };
  };

  // Traditional email/password signup (mocked)
  const signup = async (name, email, password) => {
    const users = JSON.parse(localStorage.getItem('unshackled_users') || '[]');
    
    if (users.find(u => u.email === email)) {
      return { success: false, error: 'Email already exists' };
    }
    
    const newUser = {
      id: Date.now().toString(),
      name,
      email,
      password,
      authProvider: 'email',
      quizResults: [],
      createdAt: new Date().toISOString()
    };
    
    users.push(newUser);
    localStorage.setItem('unshackled_users', JSON.stringify(users));
    
    const { password: _, ...userWithoutPassword } = newUser;
    setUser(userWithoutPassword);
    localStorage.setItem('unshackled_user', JSON.stringify(userWithoutPassword));
    
    return { success: true };
  };

  // Google OAuth login via Emergent Auth
  const loginWithGoogle = async (sessionId) => {
    try {
      // Call backend to exchange session_id for user data
      const response = await fetch(`${API_URL}/api/auth/google/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ session_id: sessionId }),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, error: errorData.detail || 'Google authentication failed' };
      }

      const userData = await response.json();
      
      // Store user in localStorage (for consistency with existing system)
      const users = JSON.parse(localStorage.getItem('unshackled_users') || '[]');
      const existingUserIndex = users.findIndex(u => u.email === userData.email);
      
      const userToSave = {
        id: userData.user_id || userData.id,
        name: userData.name,
        email: userData.email,
        picture: userData.picture,
        authProvider: 'google',
        quizResults: existingUserIndex >= 0 ? users[existingUserIndex].quizResults : [],
        createdAt: existingUserIndex >= 0 ? users[existingUserIndex].createdAt : new Date().toISOString()
      };

      if (existingUserIndex >= 0) {
        users[existingUserIndex] = { ...users[existingUserIndex], ...userToSave };
      } else {
        users.push(userToSave);
      }
      
      localStorage.setItem('unshackled_users', JSON.stringify(users));
      localStorage.setItem('unshackled_user', JSON.stringify(userToSave));
      setUser(userToSave);

      return { success: true, user: userToSave };
    } catch (error) {
      console.error('Google login error:', error);
      return { success: false, error: 'Failed to connect to server' };
    }
  };

  // Initiate Google OAuth flow
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const initiateGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('unshackled_user');
  };

  const saveQuizResult = (score, level) => {
    if (!user) return;
    
    const users = JSON.parse(localStorage.getItem('unshackled_users') || '[]');
    const userIndex = users.findIndex(u => u.id === user.id);
    
    if (userIndex !== -1) {
      const result = {
        id: Date.now().toString(),
        score,
        level,
        date: new Date().toISOString()
      };
      
      users[userIndex].quizResults = users[userIndex].quizResults || [];
      users[userIndex].quizResults.push(result);
      localStorage.setItem('unshackled_users', JSON.stringify(users));
      
      // Update current user state
      const updatedUser = { ...user, quizResults: users[userIndex].quizResults };
      setUser(updatedUser);
      localStorage.setItem('unshackled_user', JSON.stringify(updatedUser));
    }
  };

  const getQuizResults = () => {
    if (!user) return [];
    const users = JSON.parse(localStorage.getItem('unshackled_users') || '[]');
    const currentUser = users.find(u => u.id === user.id);
    return currentUser?.quizResults || [];
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isLoading, 
      login, 
      signup, 
      loginWithGoogle,
      initiateGoogleLogin,
      logout, 
      saveQuizResult,
      getQuizResults 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
